import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { PaperAirplaneIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { BookOpenIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { helpAssistantApi } from '@/services/api';
import { trackEvent, currentLocationTag } from '@/lib/analytics';
import { toComposerError, hasFriendlyMessage } from '@/utils/composerErrors';
import { MarkdownDisplay } from '@/components/shared/MarkdownDisplay';
import { docsGuideLink } from './HelpMenu';
import type { HelpAssistantSource } from '@/types/helpAssistant';

/**
 * In-app help assistant chat panel (task 545, report Pillar 5).
 *
 * A right-hand slide-over opened from the Help menu, talking to the seeded
 * `help-assistant` collaboration (task 544) through the app's standard API
 * layer. The assistant answers only from the published documentation, so every
 * answer carries the guides it came from, and a question the documentation
 * cannot answer comes back with `answered: false` — which is what puts the
 * contact-support affordance on screen rather than any prose matching here.
 *
 * Deliberately NOT plan-gated, unlike the endpoint runners: the endpoint is
 * System-owned, so the service classifies these calls as platform sessions
 * billed to the System account. A User without a plan is exactly the User most
 * likely to need help.
 *
 * No pre-flight token refresh either (Composer does one, task 395): that exists
 * because Composer forwards a token snapshot to an MCP server for the whole
 * run. The help assistant uses no tools, so the API layer's own 401
 * refresh-and-retry is the whole story.
 */

interface AssistantTurn {
  id: string;
  role: 'human' | 'assistant';
  text: string;
  /** Assistant turns only: the guides cited, and whether docs answered. */
  sources?: HelpAssistantSource[];
  answered?: boolean;
}

interface HelpAssistantPanelProps {
  open: boolean;
  onClose: () => void;
}

/** Prompts that show what the assistant is for, on the empty state. */
const STARTER_QUESTIONS = [
  'What is an endpoint?',
  'How do I describe what my AI Service takes in and gives back?',
  'How do I call my AI Service from my own application?',
];

export default function HelpAssistantPanel({ open, onClose }: HelpAssistantPanelProps) {
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [collaborationId, setCollaborationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorIsFriendly, setErrorIsFriendly] = useState(false);
  /**
   * 1-based index of the question within the conversation — the `turn` property
   * of `help_assistant_asked` (contract, task 541). A ref, not state: it is
   * read and advanced inside the submit handler and must not re-render anything.
   */
  const turnCountRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, isSending]);

  const ask = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      // `help_assistant_asked` fires at SUBMISSION, not on the answer (task 541
      // contract): the answer's quality is a separate measurement. The question
      // text never leaves the browser — only the route and the turn index.
      turnCountRef.current += 1;
      trackEvent('help_assistant_asked', {
        location: currentLocationTag(),
        turn: turnCountRef.current,
      });

      setTurns((prev) => [...prev, { id: crypto.randomUUID(), role: 'human', text: trimmed }]);
      setIsSending(true);
      setError(null);
      setErrorIsFriendly(false);

      try {
        const res = await helpAssistantApi.sendMessage({
          message: trimmed,
          ...(collaborationId ? { collaborationId } : {}),
        });

        const data = res.data.data;
        if (data.collaborationId && !collaborationId) {
          setCollaborationId(data.collaborationId);
        }

        const output = data.output;
        setTurns((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text:
              output?.answer ||
              "I didn't get an answer back that time — could you ask again?",
            sources: output?.sources ?? undefined,
            // A missing envelope is treated as unanswered, so the User still
            // gets a way forward instead of a dead end.
            answered: output ? output.answered : false,
          },
        ]);
      } catch (err) {
        const assistantError = toComposerError(err);
        setError(
          hasFriendlyMessage(assistantError)
            ? assistantError.message
            : assistantError.message || 'Unknown error'
        );
        setErrorIsFriendly(hasFriendlyMessage(assistantError));
      } finally {
        setIsSending(false);
      }
    },
    [collaborationId, isSending]
  );

  const handleSend = () => {
    void ask(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void ask(input);
      setInput('');
    }
  };

  /** New conversation: drops the server-side thread and the turn counter with it. */
  const handleReset = () => {
    setTurns([]);
    setCollaborationId(null);
    setInput('');
    setError(null);
    setErrorIsFriendly(false);
    turnCountRef.current = 0;
  };

  const isEmpty = turns.length === 0 && !isSending;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
      />
      <DialogPanel
        transition
        data-testid="help-assistant-panel"
        className="fixed inset-y-0 right-0 flex w-full max-w-md flex-col bg-white shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div>
            <DialogTitle className="text-sm font-medium text-gray-900 dark:text-white">
              Ask Innovate
            </DialogTitle>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Answers from the MonarchWorks documentation, with links to the guides.
            </p>
          </div>
          <div className="flex items-center gap-1">
            {turns.length > 0 && (
              <button
                type="button"
                onClick={handleReset}
                data-testid="help-assistant-reset"
                title="Start a new conversation"
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
                New
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="help-assistant-close"
              aria-label="Close Innovate Assistant"
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          {isEmpty && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ask a question about MonarchWorks. Try one of these:
              </p>
              <div className="space-y-2">
                {STARTER_QUESTIONS.map((question) => (
                  <button
                    key={question}
                    type="button"
                    data-testid="help-assistant-starter"
                    onClick={() => void ask(question)}
                    className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-600 dark:text-gray-300 dark:hover:border-indigo-500 dark:hover:bg-gray-700"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn) => (
            <div
              key={turn.id}
              className={`flex ${turn.role === 'human' ? 'justify-end' : 'justify-start'}`}
            >
              {turn.role === 'human' ? (
                <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-indigo-600 px-4 py-2.5 text-sm text-white">
                  {turn.text}
                </div>
              ) : (
                <div
                  data-testid="help-assistant-answer"
                  className="max-w-[92%] break-words rounded-2xl rounded-bl-sm bg-gray-100 px-4 py-2.5 text-sm text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                >
                  <MarkdownDisplay bare content={turn.text} />

                  {turn.sources && turn.sources.length > 0 && (
                    <div className="mt-3 border-t border-gray-200 pt-2 dark:border-gray-600">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {turn.sources.length === 1 ? 'Source' : 'Sources'}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {turn.sources.map((source) => (
                          <li key={source.slug}>
                            <a
                              href={docsGuideLink(source.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="help-assistant-source"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                            >
                              <BookOpenIcon className="h-3.5 w-3.5" />
                              {source.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {turn.answered === false && (
                    <div className="mt-3 border-t border-gray-200 pt-2 dark:border-gray-600">
                      {/*
                        Task 607: the fallback for a question the docs cannot
                        answer is now the in-app support form, not a mailto.
                        The location tag the analytics events already use
                        doubles as the page context handed to `/support`, and
                        the panel closes on the way so the form is not left
                        behind an overlay.
                      */}
                      <Link
                        to="/support"
                        state={{ from: currentLocationTag() }}
                        onClick={onClose}
                        data-testid="help-assistant-contact-support"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        <EnvelopeIcon className="h-3.5 w-3.5" />
                        Contact support
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {isSending && (
            <div className="flex justify-start">
              <div
                data-testid="help-assistant-thinking"
                className="rounded-2xl rounded-bl-sm bg-gray-100 px-4 py-2.5 text-sm text-gray-500 dark:bg-gray-700 dark:text-gray-400"
              >
                Looking it up…
              </div>
            </div>
          )}

          {error && (
            <div
              data-testid="help-assistant-error"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20"
            >
              <p className="text-sm text-red-700 dark:text-red-300">
                {errorIsFriendly ? error : `Something went wrong: ${error}`}
              </p>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-gray-200 p-3 dark:border-gray-700">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder="Ask a question…"
              aria-label="Ask Innovate"
              data-testid="help-assistant-input"
              disabled={isSending}
              className="flex-1 resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || !input.trim()}
              data-testid="help-assistant-send"
              aria-label="Send question"
              className="inline-flex items-center justify-center rounded-md bg-indigo-600 p-2.5 text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Innovate reads the documentation only here — it cannot see your account.
          </p>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
